<script>
    import { format } from "svelte-i18n";
    import campaignService from "../service.js";
    import { notificate } from "../../../../services/notification.js";

    let { campaign = {}, onclose, onimportsuccess } = $props();

    let importMode = $state("file"); // "file" | "paste"
    let isDragging = $state(false);
    let dragCounter = $state(0);
    let fileInput = $state();
    let csvFile = $state(null);
    let csvText = $state("");
    let isImporting = $state(false);
    let importError = $state(null);
    let importSuccess = $state(null);

    function handleDragEnter(event) {
        event.preventDefault();
        event.stopPropagation();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = "copy";
        }
        dragCounter++;
        isDragging = true;
    }

    function handleDragOver(event) {
        event.preventDefault();
        event.stopPropagation();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = "copy";
        }
        isDragging = true;
    }

    function handleDragLeave(event) {
        event.preventDefault();
        event.stopPropagation();
        dragCounter--;
        if (dragCounter <= 0) {
            dragCounter = 0;
            isDragging = false;
        }
    }

    function handleDrop(event) {
        event.preventDefault();
        event.stopPropagation();
        dragCounter = 0;
        isDragging = false;

        let droppedFile = null;
        if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
            droppedFile = event.dataTransfer.files[0];
        } else if (event.dataTransfer?.items && event.dataTransfer.items.length > 0) {
            for (let i = 0; i < event.dataTransfer.items.length; i++) {
                const item = event.dataTransfer.items[i];
                if (item.kind === "file") {
                    const f = item.getAsFile();
                    if (f) {
                        droppedFile = f;
                        break;
                    }
                }
            }
        }

        if (droppedFile) {
            processFile(droppedFile);
        }
    }

    function handleFileSelect(event) {
        if (event.target?.files && event.target.files[0]) {
            processFile(event.target.files[0]);
        }
    }

    function processFile(file) {
        csvFile = file;
        importError = null;
        importSuccess = null;
    }

    function removeSelectedFile(event) {
        if (event) event.stopPropagation();
        csvFile = null;
        if (fileInput) fileInput.value = "";
    }

    function clearCsvText() {
        csvText = "";
        importError = null;
        importSuccess = null;
    }

    async function handleImport() {
        if (!campaign?.id) return;
        importError = null;
        importSuccess = null;

        let fileToUpload = null;
        if (importMode === "file") {
            if (!csvFile) {
                importError = $format("phishing.campaign.no_data_to_import");
                return;
            }
            fileToUpload = csvFile;
        } else {
            if (!csvText.trim()) {
                importError = $format("phishing.campaign.no_data_to_import");
                return;
            }
            fileToUpload = new File([csvText], `import_${campaign.id}.csv`, { type: "text/csv" });
        }

        isImporting = true;
        try {
            const response = await campaignService.importCampaignResults(campaign.id, {}, [fileToUpload]);
            importSuccess = response;

            notificate("save_ok", "toast", { data: $format("phishing.campaign.import_button") });

            // Clear inputs on success
            csvFile = null;
            csvText = "";
            if (fileInput) fileInput.value = "";

            if (onimportsuccess) {
                onimportsuccess(response, campaign.id);
            }
        } catch (err) {
            console.error("Error importing CSV:", err);
            notificate("save_error", "toast", { data: $format("phishing.campaign.import_button") });

            if (err?.detail) {
                if (typeof err.detail === "string") {
                    importError = err.detail;
                } else if (Array.isArray(err.detail)) {
                    importError = err.detail
                        .map((d) => (d.msg ? `${d.loc ? d.loc.join(".") + ": " : ""}${d.msg}` : JSON.stringify(d)))
                        .join("; ");
                } else {
                    importError = JSON.stringify(err.detail);
                }
            } else {
                importError = err?.message || $format("notifications.network_error");
            }
        } finally {
            isImporting = false;
        }
    }

    function handleClose() {
        importError = null;
        importSuccess = null;
        if (onclose) onclose();
    }
</script>

<div class="modal-header">
    <h4 class="modal-title">
        <i class="fas fa-file-upload text-primary me-2"></i>
        {$format("phishing.campaign.import_section_title")}
        {#if campaign?.name}
            - <span class="text-primary">{campaign.name}</span>
        {/if}
    </h4>
    <button
        type="button"
        class="btn-close"
        onclick={handleClose}
        aria-label="Close"
    ></button>
</div>

<div class="modal-body">
    <!-- Campaign Stats Summary Row with Required Colors:
         Sent: blue, Opened: orange, Clicked: red, Credentials: purple, Reported: green -->
    <div class="p-3 bg-light rounded mb-4 border d-flex flex-wrap gap-2 justify-content-between align-items-center">
        <div>
            <strong>{$format("phishing.campaign.singular")}:</strong>
            <span>{campaign?.name || "-"}</span>
            {#if campaign?.description}
                <div class="text-muted small">{campaign.description}</div>
            {/if}
        </div>
        <div class="d-flex flex-wrap gap-2">
            <span>
                {$format("phishing.campaign.sent_count")}: {@html campaign?.sent_count ?? 0}
            </span>
            <span>
                {$format("phishing.campaign.opened_count")}: {@html campaign?.opened_count ?? 0}
            </span>
            <span>
                {$format("phishing.campaign.clicked_count")}: {@html campaign?.clicked_count ?? 0}
            </span>
            <span>
                {$format("phishing.campaign.submitted_count")}: {@html campaign?.submitted_count ?? 0}
            </span>
            <span>
                {$format("phishing.campaign.reported_count")}: {@html campaign?.reported_count ?? 0}
            </span>
        </div>
    </div>

    <div class="section-container">
        <div class="mb-2">
            <p class="text-muted small mb-0">
                {$format("phishing.campaign.import_section_desc")}
            </p>
        </div>

        <!-- Mode Selection Buttons -->
        <div class="btn-group btn-group-sm w-100 mb-3 mt-2" role="group">
            <button
                type="button"
                class="btn {importMode === 'file' ? 'btn-primary' : 'btn-outline-primary'}"
                onclick={() => {
                    importMode = "file";
                    importError = null;
                }}
            >
                <i class="fas fa-file-upload me-1"></i>
                {$format("phishing.campaign.upload_tab")}
            </button>
            <button
                type="button"
                class="btn {importMode === 'paste' ? 'btn-primary' : 'btn-outline-primary'}"
                onclick={() => {
                    importMode = "paste";
                    importError = null;
                }}
            >
                <i class="fas fa-paste me-1"></i>
                {$format("phishing.campaign.paste_tab")}
            </button>
        </div>

        {#if importMode === "file"}
            <!-- Hidden native file input outside the dropzone to avoid event bubbling issues -->
            <input
                type="file"
                bind:this={fileInput}
                onchange={handleFileSelect}
                accept=".csv,text/csv"
                hidden
            />

            <!-- Drag & Drop Zone using fa-file-upload icon -->
            <div
                class="dropzone {isDragging ? 'active' : ''}"
                ondragenter={handleDragEnter}
                ondragover={handleDragOver}
                ondragleave={handleDragLeave}
                ondrop={handleDrop}
                onclick={() => fileInput.click()}
                role="button"
                tabindex="0"
                onkeydown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        fileInput.click();
                    }
                }}
            >
                <div class="dropzone-content">
                    <i class="fas fa-file-upload fa-3x text-secondary mb-2"></i>
                    {#if isDragging}
                        <p class="mb-0 text-primary fw-bold">
                            {$format("phishing.campaign.drop_active")}
                        </p>
                    {:else}
                        <p class="mb-0">
                            <strong>{$format("phishing.campaign.drag_drop_title")}</strong>,
                            {$format("phishing.campaign.drag_drop_subtitle")}
                        </p>
                        <small class="text-muted">(.csv)</small>
                    {/if}
                </div>
            </div>

            {#if csvFile}
                <div class="selected-file-info alert alert-info d-flex align-items-center justify-content-between mt-3 py-2 px-3 mb-0">
                    <div>
                        <i class="fas fa-file-csv text-success fa-lg me-2"></i>
                        <strong>{csvFile.name}</strong>
                        <span class="text-muted ms-2">({(csvFile.size / 1024).toFixed(1)} KB)</span>
                    </div>
                    <button
                        type="button"
                        class="btn btn-outline-danger btn-xs"
                        onclick={removeSelectedFile}
                        title={$format("phishing.campaign.remove_file")}
                    >
                        <i class="fas fa-times"></i>
                    </button>
                </div>
            {/if}
        {:else}
            <!-- Copy-Paste Textarea -->
            <div class="mb-2">
                <div class="d-flex justify-content-between align-items-center mb-1">
                    <label for="csvTextarea" class="form-label small fw-bold mb-0">
                        {$format("phishing.campaign.csv_data")}:
                    </label>
                    {#if csvText}
                        <button
                            type="button"
                            class="btn btn-link btn-xs text-danger p-0"
                            onclick={clearCsvText}
                        >
                            <i class="fas fa-trash-alt me-1"></i>{$format("phishing.campaign.clear")}
                        </button>
                    {/if}
                </div>
                <textarea
                    id="csvTextarea"
                    class="form-control font-monospace"
                    rows="7"
                    placeholder={$format("phishing.campaign.csv_placeholder")}
                    bind:value={csvText}
                ></textarea>
            </div>
        {/if}

        <div class="mt-3">
            <button
                type="button"
                class="btn btn-primary"
                onclick={handleImport}
                disabled={isImporting || (importMode === "file" ? !csvFile : !csvText.trim())}
            >
                {#if isImporting}
                    <i class="fas fa-spinner fa-spin me-1"></i>
                    {$format("phishing.campaign.importing")}
                {:else}
                    <i class="fas fa-upload me-1"></i>
                    {$format("phishing.campaign.import_button")}
                {/if}
            </button>
        </div>

        {#if importError}
            <div class="alert alert-danger py-2 mt-3 mb-0 small">
                <i class="fas fa-exclamation-circle me-1"></i>
                {importError}
            </div>
        {/if}

        {#if importSuccess}
            <div class="alert alert-success mt-3 mb-0">
                <div class="fw-bold mb-2">
                    <i class="fas fa-check-circle me-1"></i>
                    {$format("phishing.campaign.import_success_title")}
                </div>
                <div class="d-flex flex-wrap gap-2 small">
                    <span class="badge bg-light text-dark border">
                        {$format("phishing.campaign.total_rows")}: {importSuccess.total_rows ?? 0}
                    </span>
                    <span class="badge bg-light text-dark border">
                        {$format("phishing.campaign.imported_rows")}: {importSuccess.imported_rows ?? 0}
                    </span>
                    <span class="badge bg-primary">
                        {$format("phishing.campaign.sent_count")}: {importSuccess.sent_count ?? 0}
                    </span>
                    <span class="badge badge-opened">
                        {$format("phishing.campaign.opened_count")}: {importSuccess.opened_count ?? 0}
                    </span>
                    <span class="badge bg-danger">
                        {$format("phishing.campaign.clicked_count")}: {importSuccess.clicked_count ?? 0}
                    </span>
                    <span class="badge badge-credentials">
                        {$format("phishing.campaign.submitted_count")}: {importSuccess.submitted_count ?? 0}
                    </span>
                    <span class="badge bg-success">
                        {$format("phishing.campaign.reported_count")}: {importSuccess.reported_count ?? 0}
                    </span>
                </div>

                {#if importSuccess.errors && importSuccess.errors.length > 0}
                    <div class="mt-2 text-warning small">
                        <strong>Errores / Advertencias:</strong>
                        <ul class="mb-0 ps-3">
                            {#each importSuccess.errors as errItem}
                                <li>{typeof errItem === "string" ? errItem : JSON.stringify(errItem)}</li>
                            {/each}
                        </ul>
                    </div>
                {/if}
            </div>
        {/if}
    </div>
</div>

<div class="modal-footer">
    <button type="button" class="btn btn-default" onclick={handleClose}>
        {$format("buttons.cancel")}
    </button>
</div>

<style>
    .dropzone {
        border: 2px dashed #ccc;
        border-radius: 8px;
        padding: 30px;
        text-align: center;
        background: #fdfdfd;
        cursor: pointer;
        transition: all 0.2s ease-in-out;
    }

    .dropzone:hover,
    .dropzone.active {
        border-color: #0c67dd;
        background: #f2f5ffe5;
    }

    .dropzone-content,
    .dropzone * {
        pointer-events: none;
    }

    .font-monospace {
        font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
        font-size: 0.85rem;
    }

    .badge-opened {
        background-color: #fd7e14;
        color: #fff;
    }

    .badge-credentials {
        background-color: #6f42c1;
        color: #fff;
    }
</style>
